import { useEffect, useRef } from 'react'

import ChatMessage from '@/components/modules/chat/ChatMessage'
import useAudioPlayback from '@/hooks/useAudioPlayback'
import type { Message } from '@/types/chat'

import StreamingBubble from './StreamingBubble'

interface ChatMessageListProps {
  messages: Message[]
  streamingContent: string
  isStreaming: boolean
}

export default function ChatMessageList({
  messages,
  streamingContent,
  isStreaming,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const { playbackState, activeMessageId, toggle } = useAudioPlayback()

  useEffect(() => {
    const timeout = setTimeout(() => {
      const container = scrollRef.current
      if (!container) return

      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }, 500)

    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < container.clientHeight
    if (isNearBottom) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
    }
  }, [messages[messages.length - 1], streamingContent])

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex flex-col gap-4 p-4">
        <div className="mx-auto w-full max-w-3xl space-y-4">
          {messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              playbackState={activeMessageId === message.id ? playbackState : 'idle'}
              onToggleAudio={() => toggle(message.id)}
            />
          ))}

          {isStreaming && (
            <div className="flex w-full justify-start">
              <div className="max-w-[85%] rounded-2xl bg-muted px-4 py-3 text-sm md:max-w-[75%]">
                <StreamingBubble content={streamingContent} />
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  )
}
